import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, User } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthShell, AuthHeading } from '@/components/auth/AuthShell';
import { AuthAlert } from '@/components/auth/AuthAlert';
import { AuthStatus } from '@/components/auth/AuthStatus';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { isNetworkError, NETWORK_ERROR_MESSAGE } from '@/services/network';
import { appUrl } from '@/lib/basePath';

// Same rule as the mobile app's signup (creator-app app/signup.tsx) and the
// portal's PasswordPanel, so one password works in both.
const MIN_LENGTH = 10;

/**
 * Free BMC account for a creator who lands on the portal without buying
 * first. It is the same account as the mobile app's (same Supabase project):
 * handle_new_user creates the creator profile, so nothing is inserted here.
 * Signed in, they reach My Coach, which offers the Coach plans
 * (AccessRecovery); a purchase with this email then attaches to the account.
 */
export default function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exists, setExists] = useState(false);
  const [emailSent, setEmailSent] = useState('');

  const validate = () => {
    if (!name.trim()) return 'Your name is required.';
    if (!email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Please enter a valid email address.';
    if (password.length < MIN_LENGTH) return `Password must be at least ${MIN_LENGTH} characters.`;
    return '';
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setExists(false);
    const invalid = validate();
    if (invalid) { setError(invalid); return; }

    const address = email.trim();
    setLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: address,
        password,
        options: {
          emailRedirectTo: appUrl('/auth-callback'),
          // full_name and role are copied onto the profile by handle_new_user,
          // signup_source by on_auth_user_created_signup_source (creator-app
          // migration 20261001000000). password_set_at tells the portal's
          // password panel this account already has a password.
          data: {
            full_name: name.trim(),
            role: 'creator',
            signup_source: 'bmc_coach',
            password_set_at: new Date().toISOString(),
          },
        },
      });

      if (signUpError) {
        if (isNetworkError(signUpError)) setError(NETWORK_ERROR_MESSAGE);
        else if (/already (been )?registered|already exists/i.test(signUpError.message)) setExists(true);
        else if ((signUpError as { code?: string }).code === 'weak_password' || /weak|pwned|leaked/i.test(signUpError.message))
          setError('That password is too easy to guess, or has appeared in a known data leak. Choose a different one.');
        else if (signUpError.status === 429) setError('Too many attempts. Wait a few minutes, then try again.');
        else setError(signUpError.message || 'Sign up failed. Please try again.');
        return;
      }

      // With email confirmation on, Supabase answers an already-registered
      // address with a placeholder user that has no identities (and sends
      // nothing), so the address is not revealed to strangers. Say so plainly.
      if (data.user && (data.user.identities?.length ?? 0) === 0) { setExists(true); return; }

      if (!data.session) setEmailSent(address);
      // With a session, AuthGate moves on to My Coach.
    } catch (err: unknown) {
      setError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <AuthShell>
        <AuthStatus
          tone="success"
          icon={<Mail size={32} />}
          title="Confirm your email"
          action={
            <Link to="/auth" className="font-semibold text-primary hover:text-primary/80 transition-colors">
              Back to Sign In
            </Link>
          }
        >
          We sent a confirmation link to <span className="font-semibold text-white">{emailSent}</span>. Open it to
          finish creating your account. Check spam and promotions if it hasn't arrived in a minute.
        </AuthStatus>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthHeading
        title="Create your BMC account"
        subtitle="One login for the BMC app and BMC Coach. Already bought Coach? Sign in with your purchase email instead."
      />

      <form onSubmit={handleSignUp} className="mt-6 flex flex-col gap-4" noValidate>
        <Input
          leftIcon={<User size={18} />}
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="name"
        />
        <Input
          leftIcon={<Mail size={18} />}
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoCapitalize="none"
          inputMode="email"
        />
        <PasswordInput
          placeholder={`Password (min ${MIN_LENGTH} characters)`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
        />

        {exists && (
          <AuthAlert variant="info">
            There's already a BMC account for this email.{' '}
            <Link to="/auth" className="font-semibold underline">Sign in</Link> instead, or{' '}
            <Link to="/forgot-password" className="font-semibold underline">reset your password</Link>.
          </AuthAlert>
        )}
        {error && <AuthAlert variant="error">{error}</AuthAlert>}

        <Button type="submit" variant="primary" fullWidth loading={loading} className="mt-2">
          Create account
        </Button>
      </form>

      <p className="mt-6 text-center text-[13px] leading-relaxed text-white/40">
        By signing up you agree to our{' '}
        <Link to="/terms" className="text-white/60 underline hover:text-white/80">Terms</Link> and{' '}
        <Link to="/privacy" className="text-white/60 underline hover:text-white/80">Privacy Policy</Link>.
      </p>

      <p className="mt-6 text-center text-[15px] text-white/55">
        Already have an account?{' '}
        <Link to="/auth" className="font-semibold text-primary hover:text-primary/80 transition-colors">
          Sign In
        </Link>
      </p>
    </AuthShell>
  );
}
